import InputLabel from '@/components/Atoms/InputLabel'
import { useResponseId, useUrlType } from '@/components/hooks'
import { constants } from '@/constants'
import store, { RootState } from '@/reducers/store'
import {
    useRemoveExcludeOptionsList,
    useUpdateExcludeOptionsList,
} from '@/state/survey/formInputSlice'
import { useValidate } from '@/state/survey/formSlice'
import {
    BasicAnswerSave,
    useUpdateAnswersSubmit,
} from '@/state/survey/saveAnswerSlice'
import { Utility } from '@/utils'
import React, { ReactNode, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import poaAnswerDetails from '../../SurveyModel/poaQuestions/poaAnswerDetails'
import {
    DropDownOptionList,
    IMatrixItem,
    INode,
    nodeConfigurations,
} from '../../SurveyModel/types/nodeTypes'
import MatrixMultiple from '../../SurveyNodes/MatrixMultiple'

type BloodThinningMedicinesPoaProps = {
    options: string[]
    onChange: (selected: string[]) => void
    name: string
    node: INode
    children: ReactNode
    disabled: boolean
}

const RepeatableMatrix: React.FC<BloodThinningMedicinesPoaProps> = (
    props: BloodThinningMedicinesPoaProps
) => {
    const { node, children, disabled } = props
    const { isPatient } = useUrlType()
    const { answerResponseId } = useResponseId()
    const form = useSelector((state: RootState) => state.form)
    const updateAnswersDispatch = useUpdateAnswersSubmit()
    const addExcludeOptionsList = useUpdateExcludeOptionsList()
    const updateRemoveOptionsList = useRemoveExcludeOptionsList()
    const validateInputField = useValidate()
    const [localAnswerString, setLocalAnswerString] = useState<string>('')
    const [excludedOptions, setExcludedOptions] = useState<string[]>()
    const [loading, setLoaded] = useState<boolean>(false)

    const handleOnBlur = (rows: IMatrixItem[]) => {
        const form = store.getState().form
        const mainAnswers: BasicAnswerSave[][] = []
        if (rows) {
            const repeatRows = (row: IMatrixItem) => {
                const answers: BasicAnswerSave[] = []
                const repeatNodes = (node: INode, child: boolean) => {
                    if (node.type === constants.FormRow && node.nodes) {
                        node.nodes.map((node: INode) => {
                            repeatNodes(node, false)
                        })
                    } else {
                        const otherOption: nodeConfigurations | undefined =
                            node &&
                            node.configurations &&
                            node.configurations.find(
                                (Item) => Item.key === 'OtherOptionValue'
                            )
                        let nodeName = node.name.split('-')[0]
                        const value = form[node.name].value as string
                        if (child) {
                            nodeName = node.name.split('-')[1] // assumption: id child is true it mens value should be saved under parent name. and selected value of parent is "Other"
                        }
                        if (
                            node.nodes &&
                            form[node.name].value !== otherOption?.value
                        ) {
                            const answerObject: BasicAnswerSave = {
                                name: nodeName,
                                value: value,
                            }
                            answers.push(answerObject)
                        } else {
                            if (node.nodes)
                                node.nodes.map((node: INode) => {
                                    repeatNodes(node, true)
                                }) // child node loop
                        }
                    }
                }

                row.nodes.map((node: INode) => {
                    repeatNodes(node, false)
                }) //node loop
                mainAnswers.push(answers)
            }
            rows.map((rowElement: IMatrixItem) => {
                repeatRows(rowElement) // each raw should be repaeted
            })
            const finalAnswer: string = JSON.stringify(mainAnswers)
            validateInputField(node.name, finalAnswer, true)
            if (node.validationDependants) {
                node.validationDependants.forEach((element: string) => {
                    validateInputField(element, form[element].value, true)
                })
            }
        }
    }

    useEffect(() => {
        if (form[node.name].value !== localAnswerString) {
            if (!isPatient && answerResponseId) {
                if (form[node.name].value === '') {
                    validateInputField(node.name, '[]', true)
                }
                setTimeout(() => {
                    if (!loading) return;
                    //directly accessing updated state of form
                    const newForm = store.getState().form
                    const answers: BasicAnswerSave[] =
                        poaAnswerDetails.generateQuestionAnswerSubmitFormat(
                            newForm,
                            node
                        )
                    updateAnswersDispatch(answers, answerResponseId)
                }, 100)
                setLocalAnswerString(form[node.name].value as string)
            }
        }
    }, [form, localAnswerString])

    const getExcludeOptionNodes = () => {
        const findExcludeOptionNode = (node: INode) => {
            if (Utility.getValueFromKey(node, 'NoRepeatOptions') === 'true') {
                setExcludedOptions([node.name, ...(excludedOptions || [])])
            }
            if (node.nodes) {
                node.nodes.forEach((node: INode) => {
                    findExcludeOptionNode(node)
                })
            }
            if (node.repeatedNodes) {
                node.repeatedNodes.forEach((node: INode) => {
                    findExcludeOptionNode(node)
                })
            }
        }
        findExcludeOptionNode(node)
    }

    const clearExcludeOptionNodes = () => {
        const findExcludeOptionNode = (node: INode) => {
            if (Utility.getValueFromKey(node, 'NoRepeatOptions') === 'true') {
                updateRemoveOptionsList(node.name)
            }
            if (node.nodes) {
                node.nodes.forEach((node: INode) => {
                    findExcludeOptionNode(node)
                })
            }
            if (node.repeatedNodes) {
                node.repeatedNodes.forEach((node: INode) => {
                    findExcludeOptionNode(node)
                })
            }
        }
        findExcludeOptionNode(node)
    }

    useEffect(() => {
        if (node) {
            getExcludeOptionNodes()
        }
        return () => {
            clearExcludeOptionNodes()
        }
    }, [node])

    useEffect(() => {
        if (excludedOptions) {
            const mainAnswers = form[node.name]?.value
                ? JSON.parse(form[node.name].value as string)
                : []
            const newAnswers: DropDownOptionList = {}
            mainAnswers.forEach((answers: BasicAnswerSave[]) => {
                answers.forEach((answer) => {
                    if (excludedOptions?.includes(answer.name)) {
                        const optionItem = {
                            value: `${answer.value}`,
                            label: `${answer.value}`,
                            description: '',
                        }
                        // Initialize the array if it doesn't exist
                        if (!newAnswers[answer.name]) {
                            newAnswers[answer.name] = [optionItem]
                        } else {
                            newAnswers[answer.name] = [
                                ...newAnswers[answer.name],
                                optionItem,
                            ]
                        }
                        // updateExcludeData(optionItem, answer.name)
                    }
                })

                Object.keys(newAnswers).map((key) => {
                    addExcludeOptionsList(newAnswers[key], key)
                })
            })
        }
    }, [excludedOptions, form[node.name].value])

    const onLoadComplete = () => {
        setLoaded(true);
    }

    return (
        <div
            className={`repeated-qestion-wrap question-bottom-space-1 user-input ${Utility.getClassForWidth(node)} ${!isPatient ? 'poa-input' : ''} input-border-top`}
        >
            {node.title ? (
                <InputLabel
                    label={node.title}
                    labelClasses="input-label"
                ></InputLabel>
            ) : (
                ''
            )}
            {node.subTitle ? (
                <InputLabel
                    type="subTitle"
                    label={node.subTitle}
                    labelClasses="input-label"
                ></InputLabel>
            ) : (
                ''
            )}
            <div className="repeated-qestion">
                <MatrixMultiple
                    node={node}
                    disabled={disabled}
                    onBlur={handleOnBlur}
                    onLoadComplete={onLoadComplete}
                />
            </div>
            <div>{children}</div>
        </div>
    )
}

export default RepeatableMatrix
